// iNaturalist API service
const BASE_URL = 'https://api.inaturalist.org/v1';

// Helper function to add delay between requests to respect rate limits
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Convert miles to kilometers (iNaturalist API uses km)
const milesToKm = (miles) => miles * 1.60934;

// Map conservation status codes to display text
// iNaturalist API can return either IUCN abbreviations (EN, VU, ...) or full lowercase names
const conservationStatusMap = {
  // IUCN abbreviations
  'EX': 'Extinct (EX)',
  'EW': 'Extinct in the Wild (EW)',
  'CR': 'Critically Endangered (CR)',
  'EN': 'Endangered (EN)',
  'VU': 'Vulnerable (VU)',
  'NT': 'Near Threatened (NT)',
  'LC': 'Least Concern (LC)',
  'DD': 'Data Deficient (DD)',
  'NE': 'Not Evaluated (NE)',
  // Full lowercase names returned by iNaturalist API
  'extinct': 'Extinct (EX)',
  'extinct_in_the_wild': 'Extinct in the Wild (EW)',
  'critically_endangered': 'Critically Endangered (CR)',
  'endangered': 'Endangered (EN)',
  'vulnerable': 'Vulnerable (VU)',
  'near_threatened': 'Near Threatened (NT)',
  'least_concern': 'Least Concern (LC)',
  'data_deficient': 'Data Deficient (DD)',
  'not_evaluated': 'Not Evaluated (NE)'
};

// Map full lowercase names to IUCN abbreviation codes
const statusToCode = {
  'extinct': 'EX',
  'extinct_in_the_wild': 'EW',
  'critically_endangered': 'CR',
  'endangered': 'EN',
  'vulnerable': 'VU',
  'near_threatened': 'NT',
  'least_concern': 'LC',
  'data_deficient': 'DD',
  'not_evaluated': 'NE'
};

// Display names for categories
const categoryDisplayNames = {
  'Mammalia': 'mammal',
  'Aves': 'bird',
  'Actinopterygii': 'fish',
  'Insecta': 'insect',
  'Reptilia': 'reptile',
  'Plantae': 'plant'
};

// Helper to determine the category from taxon
const getTaxonCategory = (taxon) => {
  const ancestors = taxon.ancestor_ids || [];
  const ancestorNames = (taxon.ancestors || []).map(a => a.name);

  // Check for major taxonomic groups
  if (ancestorNames.includes('Mammalia') || taxon.name === 'Mammalia') return 'Mammalia';
  if (ancestorNames.includes('Aves') || taxon.name === 'Aves') return 'Aves';
  if (ancestorNames.includes('Actinopterygii') || taxon.name === 'Actinopterygii') return 'Actinopterygii';
  if (ancestorNames.includes('Insecta') || taxon.name === 'Insecta') return 'Insecta';
  if (ancestorNames.includes('Reptilia') || taxon.name === 'Reptilia') return 'Reptilia';
  if (ancestorNames.includes('Plantae') || taxon.name === 'Plantae') return 'Plantae';

  // Fallback based on iconic taxon
  if (taxon.iconic_taxon_name) {
    const iconicMap = {
      'Mammalia': 'Mammalia',
      'Aves': 'Aves',
      'Actinopterygii': 'Actinopterygii',
      'Insecta': 'Insecta',
      'Reptilia': 'Reptilia',
      'Plantae': 'Plantae',
      'Amphibia': 'Reptilia', // Group with reptiles for simplicity
      'Mollusca': 'Insecta', // Group with insects for simplicity
      'Arachnida': 'Insecta' // Group with insects for simplicity
    };
    return iconicMap[taxon.iconic_taxon_name] || 'Unknown';
  }

  return 'Unknown';
};

// Helper to normalize a raw status string to an IUCN code
const normalizeStatusCode = (raw) => {
  if (!raw) return 'NE';
  return statusToCode[raw] || raw.toUpperCase();
};

// Helper to get conservation status from taxon
const getConservationStatus = (taxon) => {
  if (taxon.conservation_status) {
    const raw = taxon.conservation_status.status;
    const code = normalizeStatusCode(raw);
    return {
      code,
      text: conservationStatusMap[raw] || conservationStatusMap[code] || 'Not Evaluated'
    };
  }

  // Try to get from conservation statuses array
  if (taxon.conservation_statuses && taxon.conservation_statuses.length > 0) {
    const raw = taxon.conservation_statuses[0].status;
    const code = normalizeStatusCode(raw);
    return {
      code,
      text: conservationStatusMap[raw] || conservationStatusMap[code] || 'Not Evaluated'
    };
  }

  return {
    code: 'NE',
    text: 'Not Evaluated'
  };
};

// The iNaturalist API silently caps the radius parameter at 200 km.
// For larger searches, use a bounding box instead (no cap).
const MAX_RADIUS_KM = 200;

const getLocationParams = (lat, lng, radiusKm) => {
  if (radiusKm <= MAX_RADIUS_KM) {
    return { lat: lat.toString(), lng: lng.toString(), radius: radiusKm.toString() };
  }
  // Convert circle to bounding box
  const latDelta = radiusKm / 111;
  const lngDelta = radiusKm / (111 * Math.cos(lat * Math.PI / 180));
  return {
    nelat: Math.min(lat + latDelta, 90).toString(),
    nelng: Math.min(lng + lngDelta, 180).toString(),
    swlat: Math.max(lat - latDelta, -90).toString(),
    swlng: Math.max(lng - lngDelta, -180).toString()
  };
};

/**
 * Fetch species observed in a radius around a location.
 *
 * Two-phase approach:
 *  1. species_counts (one entry per unique taxon) → discovers all threatened species
 *  2. observations (batch by taxon ID) → fetches real recorded coordinates for each species
 *
 * Uses bounding box parameters for radii > 200 km to work around the iNaturalist API
 * radius cap, which would otherwise return identical results for any large radius.
 *
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {number} radiusMiles - Radius in miles
 * @returns {Promise<Array>} Array of species data
 */
export const fetchSpeciesInRadius = async (lat, lng, radiusMiles) => {
  try {
    const radiusKm = milesToKm(radiusMiles);
    const locationParams = getLocationParams(lat, lng, radiusKm);
    const sharedParams = {
      ...locationParams,
      rank: 'species',
      quality_grade: 'research',
      verifiable: 'true'
    };

    console.log(radiusKm > MAX_RADIUS_KM
      ? `Using bounding box for ${radiusMiles}mi radius (API radius cap is ~124mi)`
      : `Using radius ${radiusKm.toFixed(1)} km`);

    // ── Phase 1: discover unique threatened species ──────────────────────────
    // species_counts returns one entry per taxon (up to 500), so a large radius
    // does not get drowned out by repeat sightings of the same few species.
    const countParams = new URLSearchParams({
      ...sharedParams,
      threatened: 'true',
      per_page: '500'
    });

    console.log('Phase 1 – fetching species counts:', `${BASE_URL}/observations/species_counts?${countParams}`);

    const countResponse = await fetch(`${BASE_URL}/observations/species_counts?${countParams}`);
    if (!countResponse.ok) throw new Error(`iNaturalist API error: ${countResponse.status}`);
    const countData = await countResponse.json();

    console.log(`Phase 1 – ${countData.results?.length || 0} unique threatened species found`);

    // Filter to species with a meaningful conservation status.
    // Option C: enforce a per-category cap so that one abundant taxon group
    // (e.g. Aves) cannot displace another when the radius grows. Each category
    // is allowed up to PER_CATEGORY_CAP species; the total across all categories
    // is therefore bounded by PER_CATEGORY_CAP × number_of_categories.
    const PER_CATEGORY_CAP = 15;
    const categoryCounts = {};
    const filteredTaxa = [];

    for (const result of (countData.results || [])) {
      const taxon = result.taxon;
      if (!taxon) continue;

      const conservationStatus = getConservationStatus(taxon);
      if (conservationStatus.code === 'NE' || conservationStatus.code === 'LC') continue;
      if (!taxon.conservation_status && (!taxon.conservation_statuses || taxon.conservation_statuses.length === 0)) continue;

      const category = getTaxonCategory(taxon);
      const currentCount = categoryCounts[category] || 0;
      if (currentCount >= PER_CATEGORY_CAP) continue;

      categoryCounts[category] = currentCount + 1;
      filteredTaxa.push({ taxon, count: result.count, conservationStatus });
    }

    if (filteredTaxa.length === 0) return [];

    // ── Phase 2: fetch real observation coordinates ──────────────────────────
    // Uses the same radius as Phase 1 so pins stay within the displayed circle.
    // Observations are sorted by distance from center before being assigned, so
    // the closest sighting is always chosen first — minimising pin drift when the
    // radius is expanded (the same closest observation tends to win either way).
    const MAX_PINS_PER_SPECIES = 5;
    const taxonIds = filteredTaxa.map(t => t.taxon.id);
    const locationMap = new Map(); // taxonId → [[lat, lng], ...]

    const obsParams = new URLSearchParams({
      ...sharedParams,
      taxon_id: taxonIds.join(','),
      per_page: '200'
    });

    console.log(`Phase 2 – fetching locations for ${taxonIds.length} species`);

    const obsResponse = await fetch(`${BASE_URL}/observations?${obsParams}`);
    if (!obsResponse.ok) throw new Error(`iNaturalist API error: ${obsResponse.status}`);
    const obsData = await obsResponse.json();

    // Helper: sort observations by distance from center and populate locationMap
    const processObservations = (observations) => {
      const valid = observations.filter(obs => obs.taxon && obs.location);
      valid.sort((a, b) => {
        const [aLat, aLng] = a.location.split(',').map(Number);
        const [bLat, bLng] = b.location.split(',').map(Number);
        const distA = (aLat - lat) ** 2 + (aLng - lng) ** 2;
        const distB = (bLat - lat) ** 2 + (bLng - lng) ** 2;
        return distA - distB;
      });
      for (const obs of valid) {
        const existing = locationMap.get(obs.taxon.id) || [];
        if (existing.length < MAX_PINS_PER_SPECIES) {
          const [obsLat, obsLng] = obs.location.split(',').map(Number);
          existing.push([obsLat, obsLng]);
          locationMap.set(obs.taxon.id, existing);
        }
      }
    };

    processObservations(obsData.results || []);

    // Fallback: fetch observations individually for species that got zero results
    const missingIds = taxonIds.filter(id => !locationMap.has(id));
    if (missingIds.length > 0) {
      console.log(`Phase 2 fallback – ${missingIds.length} species missing locations, fetching individually`);
      // Process in parallel batches of 5 to stay within rate limits
      const BATCH_SIZE = 5;
      for (let i = 0; i < missingIds.length; i += BATCH_SIZE) {
        const batch = missingIds.slice(i, i + BATCH_SIZE);
        const batchPromises = batch.map(async (taxonId) => {
          const fallbackParams = new URLSearchParams({
            ...sharedParams,
            taxon_id: taxonId.toString(),
            per_page: '5'
          });
          try {
            const res = await fetch(`${BASE_URL}/observations?${fallbackParams}`);
            if (!res.ok) return [];
            const data = await res.json();
            return data.results || [];
          } catch {
            return [];
          }
        });
        const batchResults = await Promise.all(batchPromises);
        for (const results of batchResults) {
          processObservations(results);
        }
        // Small delay between batches to respect rate limits
        if (i + BATCH_SIZE < missingIds.length) await delay(300);
      }
    }

    console.log(`Phase 2 – locations resolved for ${locationMap.size} of ${filteredTaxa.length} species (${missingIds.length} needed fallback)`);

    // ── Phase 3: assemble final species list ─────────────────────────────────
    const species = [];
    for (const { taxon, count, conservationStatus } of filteredTaxa) {
      const locations = locationMap.get(taxon.id);
      if (!locations || locations.length === 0) continue; // No location found — skip rather than guess terrain
      const location = locations[0]; // primary location (used by the sidebar card)

      const category = getTaxonCategory(taxon);

      let description = taxon.wikipedia_summary;
      if (!description || description.length < 20) {
        const categoryName = categoryDisplayNames[category] || category.toLowerCase();
        description = `${taxon.preferred_common_name || taxon.name} is a threatened ${categoryName} species. ${count} observation${count !== 1 ? 's' : ''} recorded in this area.`;
      } else {
        description = description.substring(0, 200) + (description.length > 200 ? '...' : '');
      }

      species.push({
        id: taxon.id,
        commonName: taxon.preferred_common_name || taxon.name,
        scientificName: taxon.name,
        category,
        conservationStatus: conservationStatus.code,
        conservationStatusText: conservationStatus.text,
        description,
        photoUrl: taxon.default_photo?.medium_url || taxon.default_photo?.url,
        photoAttribution: taxon.default_photo?.attribution,
        observationCount: count,
        lat: location[0],
        lng: location[1],
        location,
        locations // all collected sighting coordinates for map pins
      });
    }

    console.log(`Fetched ${species.length} threatened species with real locations`);
    return species;

  } catch (error) {
    console.error('Error fetching species from iNaturalist:', error);
    throw error;
  }
};

/**
 * Fetch detailed information about a specific taxon
 * @param {number} taxonId - The iNaturalist taxon ID
 * @returns {Promise<Object>} Detailed taxon information
 */
export const fetchTaxonDetails = async (taxonId) => {
  try {
    const url = `${BASE_URL}/taxa/${taxonId}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`iNaturalist API error: ${response.status}`);
    }

    const data = await response.json();

    if (data.results && data.results.length > 0) {
      const taxon = data.results[0];
      return {
        id: taxon.id,
        name: taxon.name,
        commonName: taxon.preferred_common_name,
        rank: taxon.rank,
        photos: taxon.taxon_photos?.map(tp => ({
          url: tp.photo.medium_url || tp.photo.url,
          attribution: tp.photo.attribution,
          license: tp.photo.license_code
        })) || [],
        wikipedia_summary: taxon.wikipedia_summary,
        conservation_status: taxon.conservation_status
      };
    }

    return null;

  } catch (error) {
    console.error('Error fetching taxon details from iNaturalist:', error);
    throw error;
  }
};
